import { useState, useEffect, useMemo, useCallback } from 'react';
import { Box, Stack, Snackbar, Alert, Tabs, Tab, useMediaQuery, Fab, Drawer, Badge } from '@mui/material';
import { Map as MapIcon, CameraAlt, FormatListBulleted } from '@mui/icons-material';
import './App.css';
import { getFromDB } from './Common';
import { parseCartellone, filterItems, SORTS, distanceM, votoLabel, streetViewUrl } from './lib/parse';
import TopBar from './components/TopBar';
import KpiBar from './components/KpiBar';
import MapView from './components/MapView';
import ListPanel from './components/ListPanel';
import DetailPanel from './components/DetailPanel';
import ScanView from './components/ScanView';

const EMPTY_FILTERS = { search: '', concessionarie: [], votoMin: null, stato: null, tags: [] };

export default function App() {
  const isMobile = useMediaQuery('(max-width:900px)');
  const [tab, setTab] = useState(() => window.location.hash === '#scan' ? 'scan' : 'map');
  const [positions, setPositions] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [sortKey, setSortKey] = useState('voto-desc');
  const [selected, setSelected] = useState(new Set());
  const [cluster, setCluster] = useState(true);
  const [detailId, setDetailId] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [map, setMap] = useState(null);
  const [toast, setToast] = useState(null);
  const [userPos, setUserPos] = useState(null);   // posizione per "Più vicini a me"

  const notify = useCallback((msg, severity = 'info') => setToast({ msg, severity }), []);

  const loadData = useCallback(() => {
    getFromDB(
      (pos) => setPositions(pos),
      () => notify('Errore nel caricamento dei cartelloni — controlla la connessione e riprova', 'error'),
    );
  }, [notify]);

  useEffect(() => { loadData(); }, [loadData]);
  useEffect(() => { window.location.hash = tab === 'scan' ? '#scan' : '#map'; }, [tab]);

  // quando si sceglie "Più vicini a me", acquisisci la posizione
  useEffect(() => {
    if (sortKey !== 'vicini') return;
    if (!navigator.geolocation) { notify('Geolocalizzazione non disponibile su questo dispositivo', 'error'); return; }
    navigator.geolocation.getCurrentPosition(
      (p) => setUserPos({ lat: p.coords.latitude, lon: p.coords.longitude }),
      () => notify('Posizione non disponibile — abilita il GPS per ordinare per vicinanza', 'error'),
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  }, [sortKey, notify]);

  const items = useMemo(() => positions.map(p => ({ raw: p, meta: parseCartellone(p) })), [positions]);

  const comparator = useMemo(() => {
    if (sortKey === 'vicini' && userPos) {
      return (a, b) => distanceM(userPos.lat, userPos.lon, a.raw.latitude, a.raw.longitude)
                     - distanceM(userPos.lat, userPos.lon, b.raw.latitude, b.raw.longitude);
    }
    return SORTS[sortKey].fn;
  }, [sortKey, userPos]);

  const shown = useMemo(() => {
    const f = filterItems(items, filters);
    return [...f].sort(comparator);
  }, [items, filters, comparator]);

  const detailItem = useMemo(() => items.find(i => i.raw.id === detailId) || null, [items, detailId]);

  /** Export Excel dei selezionati: 5 colonne essenziali, nell'ordinamento attivo. */
  const exportSelectedXlsx = useCallback(async () => {
    const sel = items.filter(i => selected.has(i.raw.id)).sort(comparator);
    if (!sel.length) return;
    try {
      const XLSX = await import('xlsx');
      const rows = sel.map(({ raw, meta }) => ({
        'Titolo': raw.title || `id ${raw.id}`,
        'Voto': votoLabel(meta.voto),
        'Indirizzo': raw.nomination || '',
        'Link Street View': streetViewUrl(raw),
        'Latitudine': raw.latitude,
        'Longitudine': raw.longitude,
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 45 }, { wch: 6 }, { wch: 55 }, { wch: 60 }, { wch: 12 }, { wch: 12 }];
      rows.forEach((r, i) => {   // link Street View cliccabili in Excel
        const cell = ws[XLSX.utils.encode_cell({ r: i + 1, c: 3 })];
        if (cell?.v) cell.l = { Target: String(cell.v), Tooltip: 'Apri Street View' };
      });
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Cartelloni');
      XLSX.writeFile(wb, `cartelloni-selezionati-${new Date().toISOString().slice(0, 10)}.xlsx`);
      notify(`Excel esportato: ${sel.length} cartelloni ✓`, 'success');
    } catch {
      notify("Errore nell'export Excel", 'error');
    }
  }, [items, selected, comparator, notify]);

  const openFromList = useCallback((id) => {
    const it = items.find(i => i.raw.id === id);
    if (it && map && it.raw.latitude != null) map.flyTo([it.raw.latitude, it.raw.longitude], 15);
    setDetailId(id);
    if (isMobile) setListOpen(false);
  }, [items, map, isMobile]);

  return (
    <Stack sx={{ height: '100dvh', bgcolor: 'background.default' }}>
      <TopBar filters={filters} setFilters={setFilters} sortKey={sortKey} setSortKey={setSortKey}
              selected={selected} onClear={() => setSelected(new Set())} onRefresh={loadData}
              onExportSelected={exportSelectedXlsx} isMobile={isMobile} />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ bgcolor: 'white', borderBottom: '1px solid #e2e8f0', minHeight: 42 }}>
        <Tab icon={<MapIcon fontSize="small" />} iconPosition="start" label="Mappa" value="map" sx={{ minHeight: 42 }} />
        <Tab icon={<CameraAlt fontSize="small" />} iconPosition="start" label="Scan" value="scan" sx={{ minHeight: 42 }} />
      </Tabs>

      {tab === 'map' && (
        <>
          <KpiBar items={items} filtered={shown.length} filters={filters} setFilters={setFilters} />
          <Stack direction="row" sx={{ flex: 1, minHeight: 0, p: 1.5, pt: 1, gap: 1.5 }}>
            <Box sx={{ flex: 1, minWidth: 0, borderRadius: 2, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
              <MapView items={shown} onOpen={setDetailId} setMap={setMap}
                       cluster={cluster} setCluster={setCluster} userPos={userPos} height="100%" />
            </Box>
            {!isMobile && (
              <Box sx={{ width: 400, flexShrink: 0, bgcolor: 'white', borderRadius: 2, border: '1px solid #e2e8f0', minHeight: 0 }}>
                <ListPanel items={shown} selected={selected} setSelected={setSelected} onOpen={openFromList}
                           userPos={sortKey === 'vicini' ? userPos : null} />
              </Box>
            )}
          </Stack>
          {isMobile && (
            <>
              <Fab color="primary" variant="extended" onClick={() => setListOpen(true)}
                   sx={{ position: 'fixed', bottom: 18, right: 16, zIndex: 1100 }}>
                <Badge badgeContent={selected.size} color="warning" sx={{ mr: 1 }}>
                  <FormatListBulleted />
                </Badge>
                Lista ({shown.length})
              </Fab>
              <Drawer anchor="bottom" open={listOpen} onClose={() => setListOpen(false)}
                      PaperProps={{ sx: { height: '75dvh', borderRadius: '14px 14px 0 0' } }}>
                <ListPanel items={shown} selected={selected} setSelected={setSelected} onOpen={openFromList}
                           userPos={sortKey === 'vicini' ? userPos : null} />
              </Drawer>
            </>
          )}
        </>
      )}

      {tab === 'scan' && (
        <Box sx={{ flex: 1, minHeight: 0 }}>
          <ScanView notify={notify} onDataChanged={loadData} />
        </Box>
      )}

      <DetailPanel item={detailItem} onClose={() => setDetailId(null)} onSaved={loadData} notify={notify}
                   selected={selected} setSelected={setSelected} />

      <Snackbar open={!!toast} autoHideDuration={4500} onClose={() => setToast(null)}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        {toast ? <Alert severity={toast.severity} variant="filled" onClose={() => setToast(null)}>{toast.msg}</Alert> : <span />}
      </Snackbar>
    </Stack>
  );
}
